import memoizeOne from 'memoize-one';
import {
  DATA_EXPLORER_VERIFEYE_URL,
  TAB_ALARMS,
  TAB_CLIPS,
  TAB_EVENTS,
  TAB_SIDEKICK,
  TAB_VIDEO,
} from 'vd-constants';

export const ALARMS_URL = '/alarms';
export const CLIPS_URL = '/clips';
export const DASHBOARD_URL = '/dashboard';
export const DISCOVER_URL = '/discover';
export const EVENTS_URL = '/events';
export const SETTINGS_URL_PREFIX = '/settings';
export const VIDEO_URL_PREFIX = '/video';
export const VIDEO_LIST_URL = '/video/list';
export const VIDEO_MAP_URL = '/video/map';
export const VIDEO_VISUALIZATION_URL = '/video/visualization';
export const VIDEO_FLOORPLAN_URL = '/video/floorplan';
export const CLOUD_CAMERA_URL = '/cloudCamera';
export const SETTINGS_CLOUD_RECORDINGS_URL = '/settings/recordings';
export const SIDEKICK_URL = '/sidekick';
export const VERIFEYE4_URL = '/verifeye4';

/**
 * Validates if a URL is safe to use in href attributes.
 * Prevents XSS attacks by ensuring the URL uses a safe protocol.
 * @param url - The URL to validate
 * @returns true if the URL is safe, false otherwise
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  try {
    const parsedUrl = new URL(url);
    const protocol = parsedUrl.protocol.toLowerCase();

    // Only allow http, https, mailto, and tel protocols
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];

    return allowedProtocols.includes(protocol);
  } catch {
    // If URL parsing fails, it's not a valid URL
    return false;
  }
}

/**
 * Safely sanitizes a URL for use in href attributes.
 * Returns the original URL if valid, or undefined if invalid.
 * @param url - The URL to sanitize
 * @returns The sanitized URL or undefined if invalid
 */
export function sanitizeUrl(url: string | undefined): string | undefined {
  return url && isValidUrl(url) ? url : undefined;
}

interface TabDefinition {
  urlPrefix: string;
  tabName: string;
}

const TABS: TabDefinition[] = [
  {
    urlPrefix: CLIPS_URL,
    tabName: TAB_CLIPS,
  },
  {
    urlPrefix: DASHBOARD_URL,
    tabName: 'Dashboard',
  },
  {
    urlPrefix: DISCOVER_URL,
    tabName: 'Discover',
  },
  {
    urlPrefix: EVENTS_URL,
    tabName: TAB_EVENTS,
  },
  {
    urlPrefix: SETTINGS_URL_PREFIX,
    tabName: 'Settings',
  },
  {
    urlPrefix: VIDEO_URL_PREFIX,
    tabName: TAB_VIDEO,
  },
  {
    urlPrefix: ALARMS_URL,
    tabName: TAB_ALARMS,
  },
  {
    urlPrefix: CLOUD_CAMERA_URL,
    tabName: 'Cloud Camera',
  },
  {
    urlPrefix: SIDEKICK_URL,
    tabName: TAB_SIDEKICK,
  },
  {
    urlPrefix: VERIFEYE4_URL,
    tabName: 'VerifEye4',
  },
  {
    urlPrefix: DATA_EXPLORER_VERIFEYE_URL,
    tabName: 'VerifEye',
  },
];

export const getTabFromPathname = memoizeOne((pathname: string) => {
  const tab = TABS.find((tab) => pathname.startsWith(tab.urlPrefix));
  return tab?.tabName;
});

export const ALARMS_EVENT_BASE_URL = '/alarms/new';
export const ALARMS_DATE_FILTER_PARAM = 'alarmDate';

export const eventsEventPlayerUrl = (eventId: string) => `/events/${eventId}`;
export const clipsEventPlayerUrl = (eventId: string) => `/clips/${eventId}`;

/** Load the event player for a specific alarm. */
export const alarmsEventPlayerUrl = (
  eventId: string,
  options: {
    date?: Date;
  } = {},
): string => {
  const queryString = toQueryString({
    [ALARMS_DATE_FILTER_PARAM]: options.date ? `${options.date.valueOf()}` : undefined,
  });
  return `${ALARMS_EVENT_BASE_URL}/${eventId}${queryString}`;
};

/** Load the alarms page. */
export function alarmsPageUrl(
  options: {
    date?: Date;
  } = {},
): string {
  const queryString = toQueryString({
    [ALARMS_DATE_FILTER_PARAM]: options.date ? `${options.date.valueOf()}` : undefined,
  });
  return `${ALARMS_EVENT_BASE_URL}${queryString}`;
}

function toQueryString(params: { [key: string]: string | undefined }) {
  let hasContent = false;

  const query = new URLSearchParams();
  for (const key of Object.keys(params)) {
    const val = params[key];
    if (val !== undefined) {
      hasContent = true;
      query.set(key, val);
    }
  }

  if (!hasContent) {
    return '';
  }
  return `?${query.toString()}`;
}
